import type { DataFetcherParams } from '@axiom/ui';
import {
  Button,
  CondensedHeader,
  DataGrid,
  AsyncConnector,
  DataGridHeader,
  Layout,
  LayoutItem,
  useApi,
} from '@axiom/ui';
import React, { useMemo, useState } from 'react';
import type { Opportunity, User } from '@axiom/validation';
import { useNavigate, Link } from 'react-router';

import type {
  readUserOpportunityResponseType,
  WorkspaceFilterType,
} from '../../api/user-opportunity-api';
import {
  UserOpportunityApi,
  WorkspaceTypes,
} from '../../api/user-opportunity-api';
import { UserFullNameUtil } from '../../utils/user-full-name-util';
import { OPPORTUNITY_STAGE_OPTIONS } from '../../utils/opportunity-util';
import { LegacyUserApi } from '../../api/legacy-user-api';

import { WorkspaceTimeToSubmission } from './WorkspaceTimeToSubmission';
import { WORKSPACE_FILTER_KEYS } from './workspace.const';
import type { WorkspaceFilters } from './workspace.types';
import { WorkspaceContextMenuItems } from './WorkspaceContextMenuItems';

export const baseWorkspaceNoRowMessageCopy =
  'Opportunities that are assigned to you will appear here';
export const filteredWorkspaceNoRowMessageCopy =
  'Not seeing any opportunities? Try updating or clearing your filters.';

type WorkspaceOpportunity = Opportunity & {
  positionsLength: number;
  displayedPositionId?: string | null;
  displayedPositionJobDescription?: string | null;
};

const allStages = ['Stage 0b', 'Stage 0c', 'Stage 1', 'Stage 2', 'Stage 3'];

export const WorkspaceGrid = ({ user }: { user: User }) => {
  const [{ data: users }] = useApi(LegacyUserApi.readUsers());
  const [resultCount, setResultCount] = useState<number>(0);
  const [filters, setFilters] = useState<WorkspaceFilters>({
    type: WorkspaceTypes.accountManagerOwnerOrSalesLead,
    stage: allStages,
  });

  // Memoize filters to ensure stable reference - only changes when values actually change
  // This prevents unnecessary GridConnector recreations if setFilters is called multiple times
  const filtersKey = JSON.stringify(filters);
  const stableFilters = useMemo(() => filters, [filtersKey]);

  const navigate = useNavigate();

  const mapFulfillmentActive = (selectedStages: string[]) => {
    if (allStages.every(s => selectedStages.includes(s))) {
      return null;
    } else if (selectedStages.length === 1 && selectedStages[0] === '3') {
      return false;
    }
    return true;
  };

  const mapSortFields = (sortObject: DataFetcherParams['sort']) => {
    if (Object.values(sortObject)[0] === null) {
      return 'stageCode';
    }
    const sortField = Object.keys(sortObject)[0];
    switch (sortField) {
      case 'tsLeadName': {
        return 'ownerUser';
      }
      case 'sellerName': {
        return 'salesLead';
      }
      case 'positionsLength': {
        return 'positions';
      }
      default: {
        return sortField || 'stageCode';
      }
    }
  };
  // Recreate GridConnector only when stableFilters actually changes
  const GridConnector = useMemo(
    () =>
      new AsyncConnector<readUserOpportunityResponseType>({
        getData: args => {
          return UserOpportunityApi.readUserOpportunities(user.id, args.page, {
            ...stableFilters,
            isFulfillmentActive: mapFulfillmentActive(stableFilters.stage),
            stage: stableFilters.stage.map(st => st.replace('Stage ', '')),

            sort: mapSortFields(args.sort),
            order: Object.values(args.sort)[0] || undefined,
          } as WorkspaceFilterType);
        },
        parseData: results => {
          setResultCount(results.meta.resultCount);
          const modifiedResults: WorkspaceOpportunity[] = [];
          results.data.forEach(opp => {
            const workspaceOpportunity: WorkspaceOpportunity = {
              ...opp,
              positionsLength: opp.positions.length,
              displayedPositionId: opp.positions[0]?.id,
              displayedPositionJobDescription: opp.positions[0]?.description,
            };
            modifiedResults.push(workspaceOpportunity);
          });
          return {
            currentPage: results.meta.currentPage,
            totalPages: results.meta.pageCount,
            data: modifiedResults,
          };
        },
        onUnmount: () => {
          UserOpportunityApi.clearReadUserOpportunities(user.id);
        },
      }),
    [user.id, stableFilters]
  );

  const timeToSubmissionCell = ({ data }: { data: WorkspaceOpportunity }) => {
    return Number(data.stageCode) > 0 ? (
      <WorkspaceTimeToSubmission opportunity={data} />
    ) : (
      ''
    );
  };

  const opportunityNameCell = ({ data }: { data: WorkspaceOpportunity }) => {
    const oppData = data;
    return (
      <Link to={`/opportunity-detail/${oppData.id}`}>{oppData.jobName}</Link>
    );
  };

  const contextMenuCell = ({ data }: { data: WorkspaceOpportunity }) => {
    const { id, displayedPositionId } = data;
    return (
      <Button
        name="OPEN_JOB_DESCRIPTION_BUTTON"
        pattern="secondary"
        variation="minimal"
        onClick={() => {
          void navigate(
            `/opportunity-detail/${id}?showMoreEnabled=${displayedPositionId}`
          );
        }}
      >
        Open
      </Button>
    );
  };

  return (
    <Layout direction="vertical" stretched>
      <LayoutItem topGutter="24px" bottomGutter="8px">
        <CondensedHeader name="OPP_COUNT">
          {resultCount} {resultCount === 1 ? 'Opportunity' : 'Opportunities'}
        </CondensedHeader>
      </LayoutItem>
      <LayoutItem stretched>
        <DataGrid<readUserOpportunityResponseType>
          name="WORKSPACE_GRID"
          displayMode="infinite"
          dataSource={GridConnector}
          noResultsMessage={
            filters.type === WorkspaceTypes.accountManagerOwnerOrSalesLead &&
            allStages.every(s => filters.stage.includes(s))
              ? baseWorkspaceNoRowMessageCopy
              : filteredWorkspaceNoRowMessageCopy
          }
        >
          <DataGridHeader<WorkspaceOpportunity>
            name="timeToSubmissionHours"
            displayName="TTS"
            sortingOrder={['asc', 'desc', null]}
            width={136}
            cellRender={timeToSubmissionCell}
          />
          <DataGridHeader<WorkspaceOpportunity>
            name="jobName"
            displayName="Opportunity"
            sortingOrder={['asc', 'desc', null]}
            cellRender={opportunityNameCell}
          />
          <DataGridHeader<WorkspaceOpportunity>
            name="stageCode"
            displayName="Stage"
            width={150}
            sortingOrder={['asc', 'desc', null]}
            FiltersRender={() =>
              WorkspaceContextMenuItems({
                filters,
                setFilters,
                values: allStages,
                updateKey: WORKSPACE_FILTER_KEYS.STAGE,
              })
            }
            cellRender={({ data }) => {
              const { stageCode } = data;
              return stageCode && OPPORTUNITY_STAGE_OPTIONS[stageCode];
            }}
          />
          <DataGridHeader<WorkspaceOpportunity>
            name="accountName"
            displayName="Account"
            sortingOrder={['asc', 'desc', null]}
          />
          <DataGridHeader<WorkspaceOpportunity>
            name="positionsLength"
            displayName="Positions"
            width={112}
            sortingOrder={['asc', 'desc', null]}
          />
          <DataGridHeader<WorkspaceOpportunity>
            name="nextStep"
            displayName="Next steps"
          />
          <DataGridHeader<WorkspaceOpportunity>
            name="tsLeadName"
            displayName="TS lead"
            width={144}
            sortingOrder={['asc', 'desc', null]}
            cellRender={({ data }) => {
              const { ownerUserId } = data;
              return UserFullNameUtil(ownerUserId, users);
            }}
          />
          <DataGridHeader<WorkspaceOpportunity>
            name="sellerName"
            displayName="Seller"
            width={144}
            sortingOrder={['asc', 'desc', null]}
            cellRender={({ data }) => {
              const { salesLeadId } = data;
              return UserFullNameUtil(salesLeadId, users);
            }}
          />
          <DataGridHeader<WorkspaceOpportunity>
            name="displayedPositionJobDescription"
            displayName="Job description"
            width={250}
            cellRender={({ data }) => {
              const { displayedPositionJobDescription } = data;
              return displayedPositionJobDescription;
            }}
          />

          <DataGridHeader<WorkspaceOpportunity>
            name="FilterByOwner"
            displayName="Filter by owner"
            FiltersRender={() =>
              WorkspaceContextMenuItems({
                filters,
                setFilters,
                values: Object.values(WorkspaceTypes),
                updateKey: WORKSPACE_FILTER_KEYS.TYPE,
              })
            }
            cellRender={() => {
              return filters.type;
            }}
          />
          <DataGridHeader<WorkspaceOpportunity>
            name="contextMenu"
            displayName=""
            pinned="right"
            lockPinned
            width={84}
            cellRender={contextMenuCell}
          />
        </DataGrid>
      </LayoutItem>
    </Layout>
  );
};
